// © Microsoft Corporation. All rights reserved.

import { BreakpointIndexToPx, LikeDislikeActionKey, Status, TelemetryConstants } from "./ContentActionsToolbar.constants";
import { BreakpointTracker, identifyBreakpoint } from "@microsoft/fast-layouts-react";
import { ButtonItemType, ButtonType } from "./components/sharing-toolbar/SharingToolbar";
import { ContentActionsToolbarActions, ContentActionsToolbarConfig, ContentActionsToolbarProps, ContentActionsToolbarState } from ".";
import { ContentActionsToolbarConnectorArgs, ContentActionsToolbarInspector, ContentActionsToolbarInspectorArgs } from "./ContentActionsToolbar.inspector";
import { DynamicReducer, IReducer } from "@msnews/experiences-redux";
import { ExperienceInitialProps, Telemetry, TelemetryProps, TelemetryTags, TelemetryTypes } from "@msnews/experiences-base";
import { IRequestContext, InspectorResponse, TelemetryBase, getAppEnvironment } from "@msnews/core";
import { OptionItemDislike, OptionItemLike, OptionItemUndoDislike, OptionItemUndoLike } from "./components/sharing-toolbar/SharingToolbar.constants";
import { SharedDocumentType, SharingHelper, ViewsStringResource } from "@msnews/views-helpers";

import { ComponentConnector } from "@msnews/experiences-connector";
import { ContentActionsToolbarAppErrors } from "@msnews/diagnostics";
import { ContentActionsToolbarServiceClient } from "./services";
import { ContentActionsToolbarTelemetryHelper } from "./ContentActionsToolbar.telemetry";
import { Store } from "redux";
import { getInnerWidth } from "@msnews/isomorphic-util";

/**
 * An interface to represent the more options menu callbacks
 * @interface MoreOptionsCallbacks
 */
interface MoreOptionsCallbacks {
    /** On like event handler */
    onLike: (event: any) => void;

    /** On dislike evewnt hander */
    onDislike: (event: any) => void;
}

/**
 * The connector for the ContentActionsToolbar Experience
 * @class
 */
export class ContentActionsToolbarConnector extends ComponentConnector<ContentActionsToolbarConfig, ContentActionsToolbarState, ContentActionsToolbarProps, any> {

    /**
     * Telemetry related props for the experience.
     * @type {TelemetryProps}
     */
    private sharingToolbarTelemetryProps: TelemetryProps;

    /**
     * Telemetry related tags for the experience.
     * @type {TelemetryProps}
     */
    private sharingToolbarTelemetryTags: TelemetryTags;

    /**
     * An instance of the content action service client
     * @type {ContentActionsToolbarServiceClient}
     */
    private actionServiceClient: ContentActionsToolbarServiceClient;

    /**
     * Flag to indicate LikeDislike button is present, so content status needs to be obtained
     * @type {boolean}
     */
    private isContentStatusRequired: boolean;

    /**
     * Telemetry related tags for the more option button.
     */
    private moreOptionsTelemetryTags: {
        Like: TelemetryTags,
        Dislike: TelemetryTags,
        UndoLike: TelemetryTags,
        UndoDislike: TelemetryTags
    };

    /**
     * Initializes the ContentActionsToolbar connector
     * @param {string} namespace Redux subspace name for the connector
     * @param {string} parentNamespace Redux subspace name for the parent connector
     * @param {DynamicReducer} rootReducer Application root reducer
     * @param {IReducer<ContentActionsToolbarState>} reducer The connector reducer
     * @param {Store<any>} store The store or redux subspace that the connector will be connected to
     * @param {ContentActionsToolbarConfig} config The configuration properties from config index
     */
    public constructor(namespace: string,
        parentNamespace: string,
        rootReducer: DynamicReducer,
        reducer: IReducer<ContentActionsToolbarState>,
        store: Store<any>,
        config?: ContentActionsToolbarConfig,
        requestContext?: IRequestContext) {
        super(namespace, parentNamespace, rootReducer, reducer, store, config, requestContext);

        this.isContentStatusRequired = config.sharingButtons.some(t => t.buttonType == ButtonType.LikeDislike);
    }

    /**
     * State mapper
     * @param state The current state for the connector
     */
    public mapStateToProps(state: ContentActionsToolbarState): ContentActionsToolbarProps {
        const contentActionsToolbarConnectorArgs: ContentActionsToolbarConnectorArgs = {
            isContentStatusRequired: this.isContentStatusRequired
        };

        const inspectorArgs: ContentActionsToolbarInspectorArgs = {
            config: this.config,
            children: {},
            state: state,
            connectorArgs: contentActionsToolbarConnectorArgs
        };

        let contentActionsToolbarProps: ContentActionsToolbarProps = {};

        if (state.documentId && state.documentData && (!this.isContentStatusRequired || state.status)) {
            const inspectorResponse: InspectorResponse<ContentActionsToolbarProps> = ContentActionsToolbarInspector.inspect(inspectorArgs);

            let likeDislikeCallbacks: MoreOptionsCallbacks;
            if (inspectorResponse.props.sharingToolbar && inspectorResponse.props.sharingToolbar.sharingButtons && inspectorResponse.props.sharingToolbar.sharingButtons.length > 0) {
                const sharingButtons = inspectorResponse.props.sharingToolbar.sharingButtons;

                let status: Status;
                if (this.isContentStatusRequired) {
                    status = inspectorResponse.props.contentStatus;
                    likeDislikeCallbacks = this.BuildLikeDislikeCallbacks(status, state.documentId);
                    this.InjectCallbacksToMoreOptionsMenu(sharingButtons.find(m => m.buttonType === ButtonType.MoreOptions), status, likeDislikeCallbacks);

                    // Set likeDislike button callback based on content status
                    sharingButtons.find(m => m.buttonType === ButtonType.LikeDislike).buttonData
                        .onSelect = (event: any) => status === Status.DisLike ? likeDislikeCallbacks.onDislike(event) : likeDislikeCallbacks.onLike(event);
                }

                ContentActionsToolbarTelemetryHelper.InjectTelemetry(sharingButtons, status, this.sharingToolbarTelemetryProps);
            }

            contentActionsToolbarProps = {
                ...inspectorResponse.props,
                rootTelemetryTags: this.sharingToolbarTelemetryTags,
                ...this.telemetryTags,
                ...likeDislikeCallbacks
            };
        }

        return contentActionsToolbarProps;
    }

    /**
     * Perform one time initialization of the connector, including getting initial state from config
     * @param {ExperienceInitialProps} connectorProps Application initial props
     */
    public async onComponentConnect(initialProps?: ExperienceInitialProps): Promise<void> {
        await ComponentConnector.prototype.onComponentConnect.call(this, initialProps);

        const { contentId, context: { staticPosition, isRSThree = false, documentType = SharedDocumentType.CMS } } = initialProps;
        if (!contentId) {
            ContentActionsToolbarActions.sharingToolbarError.getActionSender(this).send(new Error(ViewsStringResource.sharingToolbarLoadFailure));
            this.onAppError({
                ...ContentActionsToolbarAppErrors.ContentActionsSharingToolbarLoadError,
                message: `Error while initializing content actions toolbar. Requried initial props is not available: InitialProps = ${initialProps}`
            });
            return;
        }

        this.actionServiceClient = new ContentActionsToolbarServiceClient(window.fetch.bind(window));

        this.generateTelemetryTags();

        if (this.isContentStatusRequired) {
            this.getContentStatus(contentId);
        }
        this.getDocumentData(contentId, documentType);

        const currentBreakpoint = this.initializeBreakpointTracker(isRSThree);
        ContentActionsToolbarActions.initializeToolbar.getActionSender(this).send(currentBreakpoint, isRSThree, contentId, staticPosition);
    }

    /**
     * Initialize and subscribe Breakpoint tracker to detect change in toolbar position
     */
    private initializeBreakpointTracker(isRSThree: boolean): number {
        BreakpointTracker.defaultBreakpoint = identifyBreakpoint(getInnerWidth());
        BreakpointTracker.breakpoints = BreakpointIndexToPx;
        BreakpointTracker.update();
        BreakpointTracker.subscribe(breakpoint => {
            ContentActionsToolbarActions.ViewportUpdate.getActionSender(this).send(breakpoint, isRSThree);
        });

        return BreakpointTracker.currentBreakpoint();
    }

    /**
     * Fetches the current status (like/dislike/none) of the document
     * @param {ExperienceInitialProps} documentId The document Id
     */
    private async getContentStatus(documentId: string): Promise<void> {
        const contentStatus = await this.actionServiceClient.GetPreferenceInitialState(documentId);
        if (!contentStatus) {
            ContentActionsToolbarActions.sharingToolbarError.getActionSender(this).send(new Error(ViewsStringResource.sharingToolbarLoadFailure));
            this.onAppError({
                ...ContentActionsToolbarAppErrors.ContentActionsSharingToolbarLoadError,
                message: "Error while retrieving content status data from API"
            });
            return;
        }
        ContentActionsToolbarActions.ContentStatuReady.getActionSender(this).send(contentStatus);
    }

    /**
     * Fetches the required document data for the experience
     * @param {ExperienceInitialProps} documentId The document Id
     */
    private async getDocumentData(documentId: string, documentType: SharedDocumentType): Promise<void> {
        const documentData = await this.actionServiceClient.GetDocumentData(documentId);
        if (!documentData) {
            ContentActionsToolbarActions.sharingToolbarError.getActionSender(this).send(new Error(ViewsStringResource.sharingToolbarLoadFailure));
            this.onAppError({
                ...ContentActionsToolbarAppErrors.ContentActionsSharingToolbarLoadError,
                message: "Error while retrieving content status data from API"
            });
            return;
        }

        documentData.sharingShortUrl = SharingHelper.shortUrlBuilder({ documentId, documentType }, getAppEnvironment().CurrentMarket);
        ContentActionsToolbarActions.documentDataReady.getActionSender(this).send(documentData);
    }

    /**
     * Builds the like and dislike callbacks
     * @param {Status} The content status
     * @param {DocumentId} The document id
     * @returns {MoreOptionsCallbacks} The MoreOptionsCallbacks interface implementation
     */
    private BuildLikeDislikeCallbacks(status: Status, documentId: string): MoreOptionsCallbacks {
        return {
            onLike: (event: Event) => {
                if (status !== Status.Like) {
                    this.actionServiceClient.Like(documentId);
                    TelemetryBase.trackEvent({ type: "click_nonnav", target: event.currentTarget }, null, null, OptionItemLike.key.toUpperCase());
                    ContentActionsToolbarActions.likeContent.getActionSender(this).send();
                } else {
                    this.actionServiceClient.Unlike(documentId);
                    TelemetryBase.trackEvent({ type: "click_nonnav", target: event.currentTarget }, null, null, OptionItemUndoLike.key.toUpperCase());
                    ContentActionsToolbarActions.unlikeContent.getActionSender(this).send();
                }
                return;
            },
            onDislike: (event: Event) => {
                if (status !== Status.DisLike) {
                    this.actionServiceClient.Dislike(documentId);
                    TelemetryBase.trackEvent({ type: "click_nonnav", target: event.currentTarget }, null, null, OptionItemDislike.key.toUpperCase());
                    ContentActionsToolbarActions.dislikeContent.getActionSender(this).send();
                } else {
                    this.actionServiceClient.Undislike(documentId);
                    TelemetryBase.trackEvent({ type: "click_nonnav", target: event.currentTarget }, null, null, OptionItemUndoDislike.key.toUpperCase());
                    ContentActionsToolbarActions.undislikeContent.getActionSender(this).send();
                }
                return;
            }
        };
    }

    /**
     * Injects callbacks to more options menu buttons
     * @param {ButtonItemType} moreOptionsButton The more option button
     * @param {Status} status The current content status
     * @param {MoreOptionsCallbacks} The more options menu callbacks interface
     */
    private InjectCallbacksToMoreOptionsMenu(moreOptionsButton: ButtonItemType, status: Status, callbacks: MoreOptionsCallbacks): void {
        if (!moreOptionsButton || !moreOptionsButton.buttonData || !moreOptionsButton.buttonData.menuItems) {
            return;
        }
        const { menuItems } = moreOptionsButton.buttonData;

        if (status === Status.Like) {
            menuItems.find(e => e.key === LikeDislikeActionKey.undoliked).onSelect = (event: any) => callbacks.onLike(event);
            menuItems.find(e => e.key === LikeDislikeActionKey.dislike).onSelect = (event: any) => callbacks.onDislike(event);
        } else if (status === Status.DisLike) {
            menuItems.find(e => e.key === LikeDislikeActionKey.undodisliked).onSelect = (event: any) => callbacks.onDislike(event);
            menuItems.find(e => e.key === LikeDislikeActionKey.like).onSelect = (event: any) => callbacks.onLike(event);
        } else {
            menuItems.find(e => e.key === LikeDislikeActionKey.dislike).onSelect = (event: any) => callbacks.onDislike(event);
        }
    }

    /**
     * Sets base telemetry data for Content Actions Toolbar
     */
    private generateTelemetryTags(): void {
        this.sharingToolbarTelemetryProps = Telemetry.getChildTelemetryItem(this.telemetryProps, TelemetryConstants.socialToolbarTelemetryName, TelemetryTypes.Module);
        this.sharingToolbarTelemetryTags = Telemetry.render(this.sharingToolbarTelemetryProps);
        this.moreOptionsTelemetryTags = {
            Like: Telemetry.render(Telemetry.getChildTelemetryItem(this.sharingToolbarTelemetryProps, OptionItemLike.key.toUpperCase(), TelemetryTypes.Like)),
            Dislike: Telemetry.render(Telemetry.getChildTelemetryItem(this.sharingToolbarTelemetryProps, OptionItemDislike.key.toUpperCase(), TelemetryTypes.Dislike)),
            UndoLike: Telemetry.render(Telemetry.getChildTelemetryItem(this.sharingToolbarTelemetryProps, OptionItemUndoLike.key.toUpperCase(), TelemetryTypes.Unlike)),
            UndoDislike: Telemetry.render(Telemetry.getChildTelemetryItem(this.sharingToolbarTelemetryProps, OptionItemUndoDislike.key.toUpperCase(), TelemetryTypes.Undislike))
        };
    }
}